import { forwardRef, useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { useDispatch } from 'react-redux';
import { handleError } from 'src/redux-store/slices/errorSlice';
import toast from 'react-hot-toast';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { useParams } from 'react-router-dom';

interface DocumentUploadProps {
  formData?: {
    DocumentFileUpload?: any[];
  };
  ApplicationId?: string | number;
  permitName?: string;
  isButtonHide?: boolean;
  handleSubmit?: (val: any) => void;
  isEdit?: boolean;
}

const DocumentUpload = forwardRef<any, DocumentUploadProps>(({
  formData,
  ApplicationId,
  permitName = "FoodandBeverages", // Updated to match your API call
  isButtonHide = false,
  handleSubmit = () => {},
  isEdit = false
}, ref) => {
  const dispatch = useDispatch();
  const { permit, applicationID } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<any[]>([]);
  const formikRef = useRef(null);
  
  // Initialize files from formData if available
  useEffect(() => {
    if (formData?.DocumentFileUpload && formData.DocumentFileUpload.length > 0) {
      setFiles(formData.DocumentFileUpload);
    }
  }, [formData]);

  // Fetch uploaded documents
  const getUploadedDocuments = async () => {
    try {
      setLoading(true);
      const response = await httpRequest(
        'GET',
        `/uploadPDF/?ApplicationID=${applicationID || ApplicationId}&PermitID=${permit}`
      );
      
      if (response?.status === 'success') {
        const formattedData = response.data?.map(item => ({
          FileName: item?.DocumentName,
          Type: item.DocumentType,
          downloadURL: item.downloadURL,
          ApplicationDocsID: item.ApplicationDocsID
        }));
        setFiles(formattedData);
        
        // Update form data with fetched documents
        dispatch(updateFormData({ DocumentFileUpload: formattedData }));
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      dispatch(handleError("Error fetching documents"));
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    
    try {
      setUploadLoading(true);
      const uploadedFiles = [];
      
      // Upload each file individually
      for (let i = 0; i < fileList.length; i += 1) { // Fixed loop counter
        const file = fileList[i];
        
        // Create new FormData for each file upload
        const formDataForUpload = new FormData();
        
        // Important: Append file first to ensure it's included properly
        formDataForUpload.append('uploadPDF', file);
        
        // Call uploadPDF API with the proper FormData and content type
        const response = await httpRequest(
          'POST',
          `/uploadPDF/?permitName=${permitName}`,
          formDataForUpload,
          {
            'Content-Type': 'multipart/form-data'
          }
        );
        
        if (response?.status === 'success') {
          toast.success(`${file.name} uploaded successfully`);
          uploadedFiles.push({
            FileName: file.name,
            Type: file.type,
            downloadURL: response.data?.downloadURL,
            ApplicationDocsID: response.data?.ApplicationDocsID
          });
        } else {
          toast.error(`Failed to upload ${file.name}: ${response?.message}`);
        }
      }
      
      // Update files state with newly uploaded files
      const newFiles = [...files, ...uploadedFiles];
      setFiles(newFiles);
      
      // Update form data with new files
      dispatch(updateFormData({ DocumentFileUpload: newFiles }));
      
      // Update the formik values
      if (formikRef.current) {
        formikRef.current.setFieldValue('documents', newFiles);
      }
      
    } catch (error) {
      dispatch(handleError("Error uploading files"));
      toast.error("Failed to upload files. Please try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle file removal
  const handleRemove = async (index: number, file: any) => {
    // If the file has an ApplicationDocsID, it's already uploaded to the server and needs to be deleted
    if (file.ApplicationDocsID) {
      try {
        setLoading(true);
        const response = await httpRequest(
          'DELETE',
          `/uploadPDF/${file.ApplicationDocsID}`
        );
        
        if (response?.status === 'success') {
          toast.success("File removed successfully");
          const updatedFiles = files.filter((_, i) => i !== index);
          setFiles(updatedFiles);
          
          // Update form data after removing file
          dispatch(updateFormData({ DocumentFileUpload: updatedFiles }));
          
          // Update the formik values
          if (formikRef.current) {
            formikRef.current.setFieldValue('documents', updatedFiles);
          }
        } else {
          toast.error(response?.message || "Failed to remove file");
        }
      } catch (error) {
        dispatch(handleError("Error removing file"));
        toast.error("Failed to remove file. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // For files that haven't been uploaded yet, just remove from local state
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      
      // Update form data after removing file
      dispatch(updateFormData({ DocumentFileUpload: updatedFiles }));
      
      // Update the formik values
      if (formikRef.current) {
        formikRef.current.setFieldValue('documents', updatedFiles);
      }
    }
  };

  // Fetch uploaded documents on component mount
  // useEffect(() => {

  //   if (applicationID || ApplicationId) {
  //     getUploadedDocuments();
  //   }
  // }, [applicationID, ApplicationId]);
  useEffect(() => {
    // Check if applicationID or ApplicationId has an actual value (not null, undefined, or empty string)
    const hasValidAppId = (applicationID && applicationID !== "") || (ApplicationId && ApplicationId !== "");
    if (hasValidAppId && isEdit) {
      getUploadedDocuments();
    }
  }, [applicationID, ApplicationId, isEdit]);

  // Create a custom submit handler to ensure the files are included
  const onSubmit = (values) => {
    // Combine formik values with file data
    const submissionData = {
      ...values,
      DocumentFileUpload: files
    };
    
    // Call the parent's handleSubmit with the combined data
    handleSubmit(submissionData);
  };

  // Content for the FormikForm
  const documentUploadContent = (
    <Box sx={{ mt: 3 }}>
      {(loading || uploadLoading) ? (
        <LoadingOverlay />
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload supporting documents for your application (optional).
          </Typography>

          <Button
            variant="contained"
            component="label"
            sx={{ mb: 2 }}
          >
            Upload Files
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => handleUpload(e.target.files)}
            />
          </Button>

          {files.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Uploaded Files:</Typography>
              <List>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemove(index, file)}
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={file.FileName || file.name}
                      secondary={file.size ? `${(file.size / 1024).toFixed(2)} KB` : ''}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // Empty fields array since we're using custom content
  const fields: Fields[] = [];

  // Initial values for Formik
  const initialValues = {
    documents: files
  };

  return (
    <FormikForm
      innerRef={(el) => {
        // Store the formik reference
        formikRef.current = el;
        // Also assign to the ref from props if provided
        if (ref) {
          if (typeof ref === 'function') {
            ref(el);
          } else {
            ref.current = el;
          }
        }
      }}
      allFields={fields}
      formMethod={onSubmit}
      heading={'Document Upload'}
      content={documentUploadContent}
      buttonTitle={['Cancel']}
      buttonPlacement={'right'}
      initialValues={initialValues}
      isButtonHide={isButtonHide}
      enableReinitialize={true} // Add this to update form values when files change
    />
  );
});

DocumentUpload.displayName = 'DocumentUpload';

export default DocumentUpload;